import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {jwtDecode} from 'jwt-decode';
import {useNavigate, useParams} from 'react-router-dom';

import {useEditInfoRepository} from '@/dataSource/src/api/editInfo/useEditInfoRepository';
import logError, {logBreadcrumb} from '@/utils/errors/logError';

import {EDIT_INFO_ROUTES, RESOLVE_EMAIL_STATUS} from './constants';
import {EditInfoTokenPayload, EditInfoViewModelContextType} from './interfaces';
import {useEditInfoTrackers} from './useEditInfoTrackers';

const EditInfoViewModelContext = createContext<
  EditInfoViewModelContextType | undefined
>(undefined);

export function EditInfoViewModelProvider({children}: {children: ReactNode}) {
  const navigate = useNavigate();
  const {token} = useParams<{token: string}>();

  const tokenPayload = useMemo(() => {
    if (!token) return null;
    logBreadcrumb({
      level: 'info',
      category: 'editInfo.EditInfoPage.tokenPayload',
      type: 'query',
      message: 'Decoding edit info token',
      data: {token},
    });
    try {
      const tokenDecoded: EditInfoTokenPayload = jwtDecode(token);
      if (!tokenDecoded) {
        logError(new Error('Invalid token payload structure'), {
          customTitle: 'EditInfo - Invalid token payload structure',
          fileName: 'EditInfoViewModelContext',
          functionName: 'tokenPayload',
          extraTags: {userFlow: 'EditInfo'},
        });
        return null;
      }
      return tokenDecoded;
    } catch (error) {
      logError(error, {
        customTitle: 'EditInfo - Invalid token decoding',
        fileName: 'EditInfoViewModelContext',
        functionName: 'tokenPayload',
        extraTags: {userFlow: 'EditInfo'},
      });
      return null;
    }
  }, [token]);

  const parentId = tokenPayload?.parent_id;

  const {
    trackViewEditInfoNotRequested,
    trackViewEditInfoApproved,
    trackViewEditInfoError,
    trackViewEditInfoErrorNoData,
    trackViewEditInfoExpired,
  } = useEditInfoTrackers({
    parentId,
  });
  const {handleResolveEmail} = useEditInfoRepository({
    parentId,
  });

  const [isLoading, setIsLoading] = useState(true);

  const tier = tokenPayload?.tier ?? null;
  const switchAccount = tokenPayload?.switch_account ?? null;
  const isMoGold = tier?.toUpperCase().includes('GOLD') ?? false;

  useEffect(() => {
    if (!token) return;

    let isMounted = true;

    logBreadcrumb({
      level: 'info',
      category: 'editInfo.EditInfoPage.resolveEmail',
      type: 'query',
      message: 'Resolving edit info token',
      data: {parentId, tier, switchAccount, isMoGold},
    });

    setIsLoading(true);

    handleResolveEmail(token)
      .then(data => {
        if (!isMounted) return;

        if (!data) {
          trackViewEditInfoErrorNoData();
          navigate(EDIT_INFO_ROUTES.ERROR, {
            replace: true,
            state: {tier, switchAccount, isMoGold, parentId},
          });
          return;
        }

        logBreadcrumb({
          level: 'info',
          category: 'editInfo.EditInfoPage.resolveEmail',
          type: 'query',
          message: 'Resolved edit info token',
          data: {parentId, tier, switchAccount, isMoGold, tokenData: data},
        });

        const state = {
          teenName: data?.teen_first_name,
          tier,
          switchAccount,
          isMoGold,
          parentId,
        };

        switch (data?.status) {
          case RESOLVE_EMAIL_STATUS.APPROVED:
            trackViewEditInfoApproved();
            navigate(EDIT_INFO_ROUTES.SUCCESS, {replace: true, state});
            break;
          case RESOLVE_EMAIL_STATUS.CANCELED:
            trackViewEditInfoNotRequested();
            navigate(EDIT_INFO_ROUTES.REJECT, {replace: true, state});
            break;
          default:
            trackViewEditInfoError();
            navigate(EDIT_INFO_ROUTES.ERROR, {replace: true, state});
            break;
        }
      })
      .catch(error => {
        if (!isMounted) return;

        if (
          error?.data?.errors?.some(
            (err: {code: string}) => err.code === 'ErrEditInfoStatusExpired',
          )
        ) {
          trackViewEditInfoExpired();
          navigate(EDIT_INFO_ROUTES.EXPIRED, {
            replace: true,
            state: {tier, switchAccount, isMoGold, parentId},
          });
          return;
        }

        trackViewEditInfoError();
        navigate(EDIT_INFO_ROUTES.ERROR, {
          replace: true,
          state: {
            errorStatus: error?.status,
            tier,
            switchAccount,
            isMoGold,
            parentId,
          },
        });
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const value = useMemo(
    () => ({isLoading, tier, switchAccount, isMoGold}),
    [isLoading, tier, switchAccount, isMoGold],
  );

  return (
    <EditInfoViewModelContext.Provider value={value}>
      {children}
    </EditInfoViewModelContext.Provider>
  );
}

export function useEditInfoViewModel(): EditInfoViewModelContextType {
  const context = useContext(EditInfoViewModelContext);
  if (!context) {
    throw new Error(
      'useEditInfoViewModel must be used within EditInfoViewModelProvider',
    );
  }
  return context;
}
